import React, { useEffect, useMemo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { FormattedMessage, useIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import { formatNumber } from '../../../helpers/FormatNumber/formatNumber';
import { resolveCurrencySymbol } from './common/resolveCurrencySymbol';
import { externalTooltipHandler, destroyExternalTooltip } from './externalTooltipHandler';
import ChartSection from './common/ChartSection';
import s from './AdminDashboard.css';

import messages from '../../../locale/jsonMessages';

ChartJS.register(ArcElement, Tooltip, Legend);

const PlatformEarnings = ({
  totalGuestServiceFee,
  totalHostServiceFee,
  baseCurrencySymbol,
  totalAmount,
  dates
}) => {
  const intl = useIntl();
  const currencySymbol = resolveCurrencySymbol(baseCurrencySymbol, intl.locale || 'en-US');

  const chartData = useMemo(() => {
    return [
      {
        name: intl.formatMessage(messages.guestServiceFees),
        value: Number(totalGuestServiceFee).toFixed(2) || 0,
        color: '#0B0E26',
      },
      {
        name: intl.formatMessage(messages.hostServiceFees),
        value: Number(totalHostServiceFee).toFixed(2) || 0,
        color: '#5E17EB',
      },
      {
        name: intl.formatMessage(messages.completeBookingAmount),
        value: Number(totalAmount).toFixed(2) || 0,
        color: '#F1F5F9',
      },
    ];
  }, [intl, totalGuestServiceFee, totalHostServiceFee, totalAmount]);

  const total = Number(totalAmount) || 0;

  const doughnutData = useMemo(() => ({
    labels: chartData.map((d) => d.name),
    datasets: [
      {
        data: chartData.map((d) => d.value),
        backgroundColor: chartData.map((d) => d.color),
        borderWidth: 0,
      },
    ],
  }), [chartData]);

  useEffect(() => {
    return () => {
      destroyExternalTooltip();
    };
  }, []);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      tooltip: {
        enabled: false,
        position: 'nearest',
        external: (ctx) => externalTooltipHandler(ctx, currencySymbol),
      },
      legend: {
        display: false,
      },
    },
  }), [currencySymbol]);

  const plugins = useMemo(() => [
    {
      id: 'centerLabel',
      afterDraw(chart) {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const cx = (chartArea.left + chartArea.right) / 2;
        const cy = (chartArea.top + chartArea.bottom) / 2;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '700 24px Outfit';
        ctx.fillStyle = '#333';
        ctx.fillText(`${currencySymbol}${formatNumber(total.toFixed(2))}`, cx, cy);
        ctx.restore();
      },
    },
  ], [currencySymbol, total]);

  if (!chartData || chartData.length === 0 || total <= 0) {
    return (
      <div className={cx(s.platformEarningsCard, s.platformEarningsCardNoData)}>
        <h3 className={s.noDataTitle}><FormattedMessage  {...messages.platFormEarning} /></h3>
        <p className={s.noDataDescription}><FormattedMessage  {...messages.noDataAvailable} /></p>
      </div>
    );
  }


  return (
    <ChartSection
      title={<FormattedMessage  {...messages.platFormEarning} />}
      subtitle={<FormattedMessage  {...messages.feeDistributionBreakdown} />}
      dates={dates ? dates : null}
      containerClass={s.platformEarningsCard}
    >
      <div className={s.platformEarningsCardInnerGrid}>
        <div className={s.platformEarningsRowWrapper}>
          {chartData?.map((item, key) => {
            return (
              <div className={s.platformEarningsRow} key={key}>
                <div className={s.platformEarningsRowDotFlexContainer}>
                  <span className={s.platformEarningsRowDot} style={{ background: item?.color }} />
                  <span>{item?.name}</span>
                </div>
                <div>{currencySymbol}{formatNumber(item?.value)}</div>
              </div>
            )
          })}
        </div>
        <div className={s.platformEarningsChart}>
          <Doughnut data={doughnutData} options={options} plugins={plugins} />
        </div>
      </div>
    </ChartSection>
  );
};

export default withStyles(s)(PlatformEarnings);
